import { ReactNode } from "react";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/Button";

export interface BentoItem {
  name: string;
  title?: string;
  description: string;
  icon: React.ReactNode;
  Icon?: any;
  background?: ReactNode;
  href?: string;
  cta?: string;
  className?: string;
  colSpan?: number;
  status?: string;
  tags?: string[];
  meta?: string;
  hasPersistentHover?: boolean;
}

const BentoGrid = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "grid w-full auto-rows-[22rem] grid-cols-3 gap-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

const BentoCard = ({
  name,
  className,
  background,
  Icon,
  icon,
  description,
  href = "#",
  cta = "Learn more",
  status,
  tags,
  meta,
  hasPersistentHover,
}: BentoItem) => (
  <div
    key={name}
    className={cn(
      "group relative col-span-3 flex flex-col justify-between overflow-hidden rounded-xl",
      // light styles
      "bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)]",
      // dark styles
      "transform-gpu dark:bg-black dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset]",
      "transition-all duration-300",
      "hover:shadow-[0_2px_12px_rgba(0,0,0,0.03)] dark:hover:shadow-[0_2px_12px_rgba(255,255,255,0.03)]",
      "hover:-translate-y-0.5 will-change-transform",
      className,
      {
        "shadow-[0_2px_12px_rgba(0,0,0,0.03)] -translate-y-0.5": hasPersistentHover,
        "dark:shadow-[0_2px_12px_rgba(255,255,255,0.03)]": hasPersistentHover,
      }
    )}
  >
    {background && <div>{background}</div>}
    
    <div className="pointer-events-none z-10 flex transform-gpu flex-col gap-1 p-6 transition-all duration-300 group-hover:-translate-y-10">
      {Icon ? (
        <Icon className="h-12 w-12 origin-left transform-gpu text-neutral-700 transition-all duration-300 ease-in-out group-hover:scale-75" />
      ) : icon ? (
        <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-black/5 dark:bg-white/10 group-hover:bg-gradient-to-br transition-all duration-300">
          {icon}
        </div>
      ) : null}
      
      <h3 className="text-xl font-semibold text-neutral-700 dark:text-neutral-300">
        {name}
        {meta && (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
            {meta}
          </span>
        )}
      </h3>
      <p className="max-w-lg text-neutral-400">{description}</p>
      
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {tags.map((tag, i) => (
            <span
              key={i}
              className="px-2 py-1 text-xs rounded-md bg-black/5 dark:bg-white/10 backdrop-blur-sm"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>

    <div
      className={cn(
        "pointer-events-none absolute bottom-0 flex w-full translate-y-10 transform-gpu flex-row items-center p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100",
      )}
    >
      <Button variant="ghost" asChild size="sm" className="pointer-events-auto">
        <a href={href}>
          {cta}
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </a>
      </Button>
    </div>
    
    <div className="pointer-events-none absolute inset-0 transform-gpu transition-all duration-300 group-hover:bg-black/[.03] group-hover:dark:bg-neutral-800/10" />
  </div>
);

export { BentoCard, BentoGrid };